import type { HomeEntry, HomeWidget } from 'types/homeWidgets';

/** Whoever holds it publishes widgets to the firm gallery, puts them on people's Home and manages every shared one. */
export const SHARE_WITH_FIRM_PERMISSIONS = ['UPDATE.HOME_WIDGETS.SHARE_WITH_FIRM'];

/** What the gallery files a widget under. */
export const HOME_WIDGET_SUBJECTS = ['TIME', 'BILLING', 'BUSINESS_DEVELOPMENT', 'CASES', 'COMPLIANCE'] as const;
export type HomeWidgetSubject = (typeof HOME_WIDGET_SUBJECTS)[number];

/** The limits the backend checks a sharing against. */
export const GALLERY_DESCRIPTION_MAX = 280;
/** Offices, teams and named people each. */
export const MAX_RECIPIENTS = 50;

export type WidgetSharing = {
    inGallery: boolean;
    onHomes: boolean;
    /** The people it is put on today; those who added it from the gallery themselves are not counted. */
    recipientCount: number;
};

/** A widget as its owner and whoever shares with the firm read it; `sharing` is absent for anyone else. */
export type SharedHomeWidget = HomeWidget & { sharing?: WidgetSharing };

/** Why a widget sits in the locked part of a Home. */
export type WidgetLock = {
    by: { id: number; name: string };
    everyone: boolean;
    /** The names of the offices, teams and people that reach the viewer; empty when it is put on everyone. */
    for: string[];
    since: string;
};

/** An entry as Home answers it: `lock` is there on the entries of `locked`, and on none of the person's own. */
export type LockedHomeEntry = HomeEntry & { lock?: WidgetLock };

/** Who a widget is put on: everyone, or any mix of the three lists. */
export type WidgetRecipients = {
    everyone: boolean;
    officeIds: number[];
    teamIds: number[];
    employeeIds: number[];
};

/** Both switches at once. Description and subject go with the gallery, the recipients with people's Home. */
export type WidgetSharingSave = Partial<WidgetRecipients> & {
    inGallery: boolean;
    description?: string;
    subject?: HomeWidgetSubject;
    onHomes: boolean;
};

export type GalleryWidget = {
    widget: HomeWidget;
    description: string;
    subject: HomeWidgetSubject;
    /** Where it stands on the viewer's saved Home: nowhere, in their own part, or put there by the firm. */
    onHome: 'NONE' | 'OWN' | 'LOCKED';
};

export type WidgetTarget = { id: number; name: string };

/** One shared widget and whom it is shared with. Description and subject are the gallery's, absent outside it. */
export type FirmWidget = {
    widget: HomeWidget & { sharing: WidgetSharing };
    description?: string;
    subject?: HomeWidgetSubject;
    everyone: boolean;
    offices: WidgetTarget[];
    teams: WidgetTarget[];
    employees: WidgetTarget[];
    sharedBy: { id: number; name: string };
    sharedAt: string;
};
